import React, { useEffect, useState } from "react";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import axiosInstance from "../../utils/axios";
import uploadToCloudinary from "../../utils/cloudinary";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { FaSpinner, FaImage, FaEdit, FaTrash } from "react-icons/fa";

const RowSkeleton = () => (
  <div className="w-full flex items-center gap-4 p-4 rounded-md border border-gray-200 bg-white animate-pulse">
    <div className="w-16 h-16 bg-gray-200 rounded-md" />
    <div className="flex-1">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
      <div className="h-3 bg-gray-200 rounded w-1/4" />
    </div>
    <div className="w-28 h-8 bg-gray-200 rounded" />
    <div className="w-28 h-8 bg-gray-200 rounded" />
  </div>
);

const NoData = ({ onAdd }) => (
  <div className="p-8 border-2 border-dashed border-gray-300 rounded-xl text-center bg-gray-50">
    <div className="text-gray-600 mb-3">No team members found</div>
    <button
      onClick={onAdd}
      className="px-4 py-2 bg-primaryColor text-white rounded hover:opacity-90"
    >
      + Add Team Member
    </button>
  </div>
);

const ImagePreviewModal = ({ src, alt, onClose }) => {
  useBodyScrollLock(true);
  return (
    <div
      className="fixed py-20 inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-3xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-1 right-3 text-white text-2xl"
          onClick={onClose}
          aria-label="close"
        >
          ×
        </button>
        <img src={src} alt={alt} className="w-full h-auto rounded shadow-lg" />
      </div>
    </div>
  );
};

const AddEditModal = ({ initialData = null, onClose, onSaved }) => {
  useBodyScrollLock(true);

  const [form, setForm] = useState({
    profile: "",
    name: "",
    designation: "",
    sequence: "",
    status: "active",
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        profile: initialData.profile || "",
        name: initialData.name || "",
        designation: initialData.designation || "",
        sequence: initialData.sequence || "",
        status: initialData.status || "active",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile({
      preview: URL.createObjectURL(f),
      file: f,
      isUploading: true,
    });
    try {
      setUploading(true);
      const url = await uploadToCloudinary(f);
      setForm((p) => ({ ...p, profile: url }));
      setImageFile((prev) => ({ ...prev, url, isUploading: false }));
    } catch (err) {
      console.error("Upload failed", err);
      toast.error("Image upload failed");
      setImageFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // basic validation
    if (!form.name?.trim() || !form.profile?.trim()) {
      return toast.error("Name and Profile image are required");
    }
    setIsSubmitting(true);
    try {
      if (initialData) {
        const resp = await axiosInstance.put(`/teams/${initialData._id}`, {
          ...form,
          sequence: form.sequence ? Number(form.sequence) : 0,
        });
        toast.success("Team member updated");
        onSaved(resp.data.team || resp.data);
      } else {
        const resp = await axiosInstance.post(`/teams`, {
          ...form,
          sequence: form.sequence ? Number(form.sequence) : 0,
        });
        toast.success("Team member added");
        onSaved(resp.data.team || resp.data);
      }
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    } finally {
      onClose();
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6 relative">
        <h3 className="text-lg font-semibold mb-4">
          {initialData ? "Edit Team Member" : "Add Team Member"}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* image */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Profile Image *
            </label>
            <div className="flex items-center gap-4">
              <label className="w-24 h-24 relative rounded-md hover:border-2 hover:bg-gray-100 hover:border-blue-700 border-dashed overflow-hidden border border-gray-200 flex items-center justify-center cursor-pointer">
                {form.profile ? (
                  <img
                    src={imageFile?.preview || form.profile}
                    alt="profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FaImage className="text-2xl text-gray-400" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="hidden"
                />
                {imageFile?.isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </label>
              <div className="text-xs text-gray-500">
                Recommended size: square (200x200). Max 10MB.
              </div>
            </div>
          </div>

          {/* name */}
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {/* designation */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Designation
            </label>
            <input
              type="text"
              name="designation"
              value={form.designation}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {/* sequence + status */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Sequence (optional)
              </label>
              <input
                type="number"
                min="0"
                name="sequence"
                value={form.sequence}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="border px-3 py-2 rounded"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              className="px-4 py-2 rounded border"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-primaryColor text-white disabled:opacity-60"
              disabled={uploading}
            >
              {isSubmitting ? (
                <span className="inline-flex items-center gap-2">
                  <FaSpinner className="animate-spin" /> Saving
                </span>
              ) : initialData ? (
                "Save Changes"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamRow = ({
  item,
  onEdit,
  onDelete,
  onViewImage,
  onToggleStatus,
  onSequenceChange,
}) => {
  const [localSeq, setLocalSeq] = useState(item.sequence || "");
  const [savingSeq, setSavingSeq] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    setLocalSeq(item.sequence || "");
    setIsChanged(false);
  }, [item.sequence]);

  const handleSeqSave = async () => {
    if (String(localSeq) === String(item.sequence)) return;
    setSavingSeq(true);
    try {
      const resp = await axiosInstance.put(`/teams/${item._id}`, {
        sequence: localSeq ? Number(localSeq) : 0,
      });
      onSequenceChange(resp.data.team || resp.data);
      toast.success("Sequence updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update sequence");
    } finally {
      setSavingSeq(false);
      setIsChanged(false);
    }
  };

  const handleCancel = () => {
    setLocalSeq(item.sequence || "");
    setIsChanged(false);
  };

  return (
    <div className="w-full flex items-center gap-4 p-3 rounded-md border border-gray-200 bg-white hover:shadow-md transition">
      <div
        onClick={() => onViewImage(item)}
        className="w-28 flex-shrink-0 cursor-pointer "
      >
        <img
          src={item.profile}
          alt={item.name}
          className="w-28 h-32 rounded-md object-cover border"
        />
      </div>

      <div className="flex-1 h-full flex-col items-start min-w-0">
        <div className="flex items-center justify-between gap-4">
          <div className="truncate">
            <div className="font-semibold text-md text-gray-800 truncate">
              {item.name}
            </div>
            <div className="text-sm text-gray-500 truncate">
              {item.designation}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              title="Edit"
              onClick={() => onEdit(item)}
              className="p-2 rounded hover:bg-gray-100"
            >
              <FaEdit />
            </button>
            <button
              title="Delete"
              onClick={() => onDelete(item)}
              className="p-2 rounded hover:bg-red-50 text-red-600"
            >
              <FaTrash />
            </button>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs text-gray-500">Sequence:</span>
            <input
              className="w-20 border px-2 py-1 rounded text-sm"
              type="number"
              min="0"
              value={localSeq}
              onChange={(e) => {
                setLocalSeq(e.target.value);
                setIsChanged(true);
              }}
            />
            {savingSeq && <FaSpinner className="animate-spin text-sm" />}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs text-gray-500">Status:</span>
            <button
              onClick={() => onToggleStatus(item)}
              className={`px-3 py-1 rounded text-sm ${
                item.status === "active"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {item.status}
            </button>
          </div>
        </div>

        {isChanged && (
          <div className="flex justify-end gap-3 mt-1 pr-1">
            <button
              className="px-3 py-1 bg-gray-200 rounded text-sm hover:bg-gray-300"
              onClick={handleCancel}
              disabled={savingSeq}
            >
              Cancel
            </button>
            <button
              className="px-3 py-1 bg-primaryColor text-white rounded text-sm hover:opacity-90 disabled:opacity-60"
              onClick={handleSeqSave}
              disabled={savingSeq}
            >
              {savingSeq ? (
                <FaSpinner className="animate-spin text-sm mx-auto" />
              ) : (
                "Save"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminTeams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewImage, setViewImage] = useState(null);

  // fetch teams
  const fetchTeams = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get("/teams");
      const data = resp?.data?.team ?? resp?.data ?? [];
      setTeams(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch teams error:", err);
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  const filtered = teams.filter((t) => {
    const q = searchQuery?.toLowerCase?.().trim() ?? "";
    if (!q) return true;
    return (
      (t.name || "").toLowerCase().includes(q) ||
      (t.designation || "").toLowerCase().includes(q)
    );
  });

  const handleAddNewClick = () => {
    setEditMember(null);
    setIsModalOpen(true);
  };

  const handleSave = (saved) => {
    fetchTeams();
  };

  const handleEdit = (item) => {
    setEditMember(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete ${item.name}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/teams/${item._id}`);
          setTeams((prev) =>
            prev.filter((p) => p._id !== item._1d && p._id !== item._id)
          );
          toast.success("Deleted");
        } catch (err) {
          console.error(err);
          toast.error("Delete failed");
        }
      }
    });
  };

  const handleView = (item) => setViewImage(item);

  const handleToggleStatus = async (item) => {
    try {
      const updated = await axiosInstance.put(`/teams/${item._id}`, {
        status: item.status === "active" ? "inactive" : "active",
      });
      // update local
      setTeams((prev) =>
        prev.map((t) =>
          t._id === item._id ? updated.data.team || updated.data : t
        )
      );
      toast.success("Status updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  const handleSequenceChange = (updatedTeam) => {
    // Replace team in local state
    setTeams((prev) =>
      prev.map((t) =>
        t._id === (updatedTeam._id || updatedTeam.id) ? updatedTeam : t
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSearchBar
        title="Team Members Management"
        onAddNewClick={handleAddNewClick}
        onSearchClick={(q) => setSearchQuery(q)}
      />

      <div className="p-6">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <RowSkeleton key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <NoData onAdd={handleAddNewClick} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map((item) => (
              <TeamRow
                key={item._id}
                item={item}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onViewImage={handleView}
                onToggleStatus={handleToggleStatus}
                onSequenceChange={handleSequenceChange}
              />
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddEditModal
          initialData={editMember}
          onClose={() => setIsModalOpen(false)}
          onSaved={handleSave}
        />
      )}

      {viewImage && (
        <ImagePreviewModal
          src={viewImage.profile}
          alt={viewImage.name}
          onClose={() => setViewImage(null)}
        />
      )}
    </div>
  );
};

export default AdminTeams;
